import logger from '@eventbrite/client-logger';
import {
    client as braintreeClient,
    GooglePayment,
    googlePayment,
} from 'braintree-web';
import { Dispatch } from 'redux';
import { change, submit } from 'redux-form';
import {
    trackGooglePayConfirmed,
    trackGooglePayShown,
} from '../actions/eventAnalytics';
import { CHECKOUT_FORM_NAME } from '../constants';
import {
    GOOGLE_PAY_MERCHANT_ID,
    GOOGLE_PAY_MERCHANT_NAME,
    GOOGLE_PAY_SHARED_PAYLOAD,
} from '../constants/googlePay';
import { getDefaultUserCountry, isNativeApp } from '../selectors/app';
import { getbraintreeClientToken, getTotalAmount } from '../selectors/order';
import { CheckoutState } from '../state';
import { EventNamespace } from '../state/event';
import { GooglePayScriptStatus } from '../state/googlePay';
import { OrderNamespace } from '../state/order';
import {
    checkGooglePayFeatureGateIfReady,
    makePaymentInAndroidApp,
} from '../utils/googlePay';
import { getAndSetBraintreeToken } from './order/common';

declare global {
    interface Window {
        _isReadyToPaySuccessCallback: any;
        _isReadyToPayErrorCallback: any;
        _makePaymentSuccessCallback: any;
        _makePaymentErrorCallback: any;
        android: any;
    }
}

export enum GooglePayActionType {
    setCanMakePaymentsWithGooglePay = 'setCanMakePaymentsWithGooglePay',
    setGooglePayInstance = 'setGooglePayInstance',
    setGooglePayPaymentRequest = 'setGooglePayPaymentRequest',
    setHasErrorInGooglePay = 'setHasErrorInGooglePay',
    setIsLoadingGooglePay = 'setIsLoadingGooglePay',
    setGooglePayScriptStatus = 'setGooglePayScriptStatus',
}

export interface SetCanMakePaymentsWithGooglePayAction {
    type: GooglePayActionType.setCanMakePaymentsWithGooglePay;
    payload: {
        canMakePaymentsWithGooglePay: boolean;
    };
}

export interface SetGooglePayInstanceAction {
    type: GooglePayActionType.setGooglePayInstance;
    payload: {
        googlePayInstance: GooglePayment;
    };
}

export interface SetGooglePayPaymentRequestAction {
    type: GooglePayActionType.setGooglePayPaymentRequest;
    payload: {
        googlePayPaymentRequest: google.payments.api.PaymentDataRequest;
    };
}

export interface SetGooglePayScriptStatusAction {
    type: GooglePayActionType.setGooglePayScriptStatus;
    payload: {
        googlePayScriptStatus: GooglePayScriptStatus;
    };
}

export interface SetHasErrorInGooglePayAction {
    type: GooglePayActionType.setHasErrorInGooglePay;
    payload: {
        hasError: boolean;
    };
}

export interface SetIsLoadingGooglePayAction {
    type: GooglePayActionType.setIsLoadingGooglePay;
    payload: {
        isLoadingGooglePay: boolean;
    };
}

export const setCanMakePaymentsWithGooglePay = (
    canMakePaymentsWithGooglePay: boolean,
): SetCanMakePaymentsWithGooglePayAction => ({
    type: GooglePayActionType.setCanMakePaymentsWithGooglePay,
    payload: {
        canMakePaymentsWithGooglePay,
    },
});

export const setGooglePayInstance = (
    googlePayInstance: GooglePayment,
): SetGooglePayInstanceAction => ({
    type: GooglePayActionType.setGooglePayInstance,
    payload: {
        googlePayInstance,
    },
});

export const setGooglePayPaymentRequest = (
    googlePayPaymentRequest: google.payments.api.PaymentDataRequest,
): SetGooglePayPaymentRequestAction => ({
    type: GooglePayActionType.setGooglePayPaymentRequest,
    payload: {
        googlePayPaymentRequest,
    },
});

export const setGooglePayScriptStatus = (
    googlePayScriptStatus: GooglePayScriptStatus,
): SetGooglePayScriptStatusAction => ({
    type: GooglePayActionType.setGooglePayScriptStatus,
    payload: {
        googlePayScriptStatus,
    },
});

export const setHasErrorInGooglePay = (
    hasError: boolean,
): SetHasErrorInGooglePayAction => ({
    type: GooglePayActionType.setHasErrorInGooglePay,
    payload: {
        hasError,
    },
});

export const setIsLoadingGooglePay = (
    isLoadingGooglePay: boolean,
): SetIsLoadingGooglePayAction => ({
    type: GooglePayActionType.setIsLoadingGooglePay,
    payload: {
        isLoadingGooglePay,
    },
});

const getGooglePayPaymentRequest = async (
    state: CheckoutState,
    googlePayInstance: GooglePayment,
): Promise<google.payments.api.PaymentDataRequest> => {
    const { event } = state as {
        event: EventNamespace;
    };

    const paymentRequest = {
        ...GOOGLE_PAY_SHARED_PAYLOAD,
        merchantInfo: {
            merchantId: GOOGLE_PAY_MERCHANT_ID,
            merchantName: GOOGLE_PAY_MERCHANT_NAME,
        },
        transactionInfo: {
            countryCode: getDefaultUserCountry(state),
            currencyCode: event.currency || 'USD',
            totalPriceLabel: `Tickets for ${event.name.text}`,
            totalPrice: `${getTotalAmount(state)}`,
            totalPriceStatus: 'FINAL',
        },
    };

    return googlePayInstance.createPaymentDataRequest(paymentRequest);
};

const getGooglePayInstance = async (order: OrderNamespace) => {
    const braintreeToken = order.braintreeClientToken;
    const client = await braintreeClient.create({
        authorization: braintreeToken,
    });

    return googlePayment.create({
        client,
        googlePayVersion: 2,
        googleMerchantId: GOOGLE_PAY_MERCHANT_ID,
    });
};

export const initializeGooglePayPaymentRequestInWeb =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();

        if (isNativeApp(state)) {
            return;
        }

        dispatch(setIsLoadingGooglePay(true));

        try {
            await dispatch(getAndSetBraintreeToken());

            const googlePayInstance = await getGooglePayInstance(
                state.order as OrderNamespace,
            );
            const googlePayPaymentRequest = await getGooglePayPaymentRequest(
                state,
                googlePayInstance,
            );

            dispatch(setGooglePayInstance(googlePayInstance));
            dispatch(setGooglePayPaymentRequest(googlePayPaymentRequest));
        } catch (error) {
            logger.error(error);
        } finally {
            dispatch(setIsLoadingGooglePay(false));
        }
    };

export const updateCanMakePaymentsWithGooglePay =
    (canMakePayments: boolean) => (dispatch: Dispatch<CheckoutState>) => {
        // If `canMakePayments` is set to true, the feature gate will be exposed
        if (checkGooglePayFeatureGateIfReady(canMakePayments)) {
            dispatch(trackGooglePayShown());
        }
        dispatch(setCanMakePaymentsWithGooglePay(canMakePayments));
    };

export const initializeGooglePayInAndroidApp = async (
    _: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    window.android.initPayment(getbraintreeClientToken(getState()));
};

export const payWithGooglePayInWeb =
    (paymentData: google.payments.api.PaymentData, instance: GooglePayment) =>
    async (dispatch: Dispatch<CheckoutState>) => {
        try {
            const response = await instance.parseResponse(paymentData);

            dispatch(
                change(
                    CHECKOUT_FORM_NAME,
                    'payment.googlePayBraintreeNonce',
                    response?.nonce,
                ),
            );

            dispatch(trackGooglePayConfirmed());
            dispatch(submit(CHECKOUT_FORM_NAME));
        } catch (e) {
            logger.error(e);
        }
    };

export const payWithGooglePayInAndroidApp =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        makePaymentInAndroidApp(
            getState(),
            (googlePayBraintreeNonce: string) => {
                dispatch(
                    change(
                        CHECKOUT_FORM_NAME,
                        'payment.googlePayBraintreeNonce',
                        googlePayBraintreeNonce,
                    ),
                );

                dispatch(trackGooglePayConfirmed());
                dispatch(submit(CHECKOUT_FORM_NAME));
            },
            (error: string) => {
                logger.error(error);
            },
        );
    };

export type GooglePayAction =
    | SetCanMakePaymentsWithGooglePayAction
    | SetGooglePayInstanceAction
    | SetGooglePayPaymentRequestAction
    | SetGooglePayScriptStatusAction
    | SetHasErrorInGooglePayAction
    | SetIsLoadingGooglePayAction;
